import {Injectable} from '@angular/core';
import {
  BaseCheckout, CheckoutFiscal,
  FiscalBasicOperationEnum,
  FiscalCashOperationEnum,
  FiscalCashRequest,
  FiscalRequest,
  FiscalResponse,
  FiscalService as Api,
  FiscalShiftOperationEnum,
  FiscalShiftRequest,
  FiscalStatusEnum, PriceInfoEnum, RefundTickets,
  Scrap,
  Shift,
  ShiftService,
  ShiftStatusEnum
} from '../../generated/sales-terminal-client';
import {LanguageService} from '../language/language.service';
import {StorageService} from '../storage/storage.service';
import {AuthService} from '../auth/auth.service';
import {SnackService} from '../snack/snack.service';
import {UtilService} from '../util/util.service';
import {TranslateService} from '@ngx-translate/core';
import {PriceInCentsPipe} from '../../pipes/priceInCents/priceInCents.pipe';
import * as FileSaver from 'file-saver-es';
import {Observable, tap} from 'rxjs';

@Injectable()
export class FiscalService {
  static STORAGE_FISCAL_TOKEN = 'fiscal.token';

  private _token: number = null;
  private _shift: Shift = null;
  private _interval = 5 * 1000; // 5 seconds
  public isOperationPending: boolean;
  public isTransaction: boolean;

  constructor(private api: Api,
              private shiftService: ShiftService,
              private languageService: LanguageService,
              private storageService: StorageService,
              private authService: AuthService,
              private snackService: SnackService,
              private translateService: TranslateService,
              private utilService: UtilService,
              private priceInCentsPipe: PriceInCentsPipe) {
    if (storageService.get(FiscalService.STORAGE_FISCAL_TOKEN)) {
      this.token = parseFloat(storageService.get(FiscalService.STORAGE_FISCAL_TOKEN));
      this.isOperationPending = true;
      this.getOperationStatus();
    }
  }

  get token() {
    return this._token;
  }

  set token(value) {
    this._token = value;
    this.storageService.set(FiscalService.STORAGE_FISCAL_TOKEN, value);
  }

  removeToken() {
    this._token = null;
    this.storageService.remove(FiscalService.STORAGE_FISCAL_TOKEN);
  }

  get shift() {
    return this._shift;
  }

  set shift(value) {
    this._shift = value;
  }

  get isReady() {
    return this.shift && [ShiftStatusEnum.Started, ShiftStatusEnum.StartedPreviousUnfinished].indexOf(this.shift.status) !== -1 && this.shift.start && !this.shift.end;
  }

  set fiscal(value: FiscalResponse) {
    this.token = value.operationToken;
    if (value.shift) {
      this.shift = value.shift;
    }
    this.isOperationPending = true;
    this.printFiscal();
  }

  public getShiftStatus(): void {
    this.shiftService.shiftStatusGet().pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    ).subscribe((shift) => {
      this.shift = shift;
      if (!this.shift && this.authService.checkSalespointProperty('cashier_auto_start')) {
        this.startDay();
      }
    });
  }

  public getOperationStatus(): void {
    if (this.isOperationPending) {
      this.api.fiscalStatusIdGet(this.token).pipe(
        tap({
          error: (e) => {
            if (this.isOperationPending) {
              setTimeout(() => this.getOperationStatus(), this._interval);
            }
          }
        })
      ).subscribe((response) => {
        if (response.fiscal?.status === FiscalStatusEnum.Pending) {
          setTimeout(() => this.getOperationStatus(), this._interval);
        } else {
          this.isOperationPending = false;
          this.removeToken();
          if (response.fiscal.status === FiscalStatusEnum.Success) {
            if (response.shift) {
              this.shift = response.shift;
            }
            this.snackService.showSuccess(this.translateService.instant(response.fiscal.message));
            this.utilService.fiscalOperationFinished.emit();
          } else if (response.fiscal.status === FiscalStatusEnum.Failure) {
            this.snackService.showError(this.translateService.instant(response.fiscal.message));
            this.utilService.fiscalOperationFailed.emit();
          }
        }
      });
    }
  }

  public printFiscal(): void {
    let fiscalObservable: Observable<Blob> = this.api.fiscalTransactionPrintIdGet(this.token, this.languageService.selectedLang, 'regular');
    if (!this.isTransaction) {
      fiscalObservable = this.api.fiscalPrintIdGet(this.token);
    }
    this.isTransaction = false;
    fiscalObservable.pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    ).subscribe((blob) => {
      const fileData = UtilService.getFileData('json_fiscal', 'json');
      let file: any;
      if (fileData.firefox) {
        file = new File([blob], fileData.filename, {type: fileData.mime});
        window.location.href = URL.createObjectURL(file);
      } else {
        file = new Blob([blob], {type: fileData.mime});
        FileSaver.saveAs(file, fileData.filename);
      }
      this.getOperationStatus();
    });
  }

  public printTransaction(priceInfo: PriceInfoEnum): Observable<Blob> {
    return this.api.fiscalTransactionPrintIdGet(this.token, this.languageService.selectedLang, priceInfo).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public cancelOperation(): void {
    this.api.fiscalCancelIdPost(this.token).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    ).subscribe(() => {
      this.getShiftStatus();
      this.removeToken();
      this.isOperationPending = false;
      this.utilService.fiscalOperationFailed.emit();
    });
  }

  public cut(): void {
    this.fiscalOperation(FiscalBasicOperationEnum.Cut).subscribe((response) =>
      this.fiscal = response);
  }

  public feed(): void {
    this.fiscalOperation(FiscalBasicOperationEnum.Feed).subscribe((response) =>
      this.fiscal = response);
  }

  public openCashRegister(): void {
    this.fiscalOperation(FiscalBasicOperationEnum.Opencashreg).subscribe((response) =>
      this.fiscal = response);
  }

  public xReport(): void {
    this.fiscalOperation(FiscalBasicOperationEnum.Xreport).subscribe((response) =>
      this.fiscal = response);
  }

  public fiscalOperation(operation: FiscalBasicOperationEnum): Observable<FiscalResponse> {
    return this.api.fiscalOperationPost({operation: operation} as FiscalRequest).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public startDay(): void {
    this.fiscalShift(FiscalShiftOperationEnum.Startshift).subscribe((response) =>
      this.fiscal = response);
  }

  public endDay(): void {
    this.fiscalShift(FiscalShiftOperationEnum.Endshift).subscribe((response) =>
      this.fiscal = response);
  }

  public fiscalShift(operation: FiscalShiftOperationEnum): Observable<FiscalResponse> {
    return this.api.fiscalShiftPost({operation: operation} as FiscalShiftRequest).pipe(
      tap({
        error: (e) => {
          if (e.error?.message === 'api_fiscal_shift_not_finished') {
            this.endDay();
          } else {
            this.snackService.showBackendError(e);
          }
        }
      })
    );
  }

  public cashIn(amount: number): void {
    this.fiscalCash(FiscalCashOperationEnum.Insertcash, this.priceInCentsPipe.transform(amount))
      .subscribe((response) => this.fiscal = response);
  }

  public cashOut(amount: number): void {
    this.fiscalCash(FiscalCashOperationEnum.Withdrawcash, this.priceInCentsPipe.transform(amount))
      .subscribe((response) => this.fiscal = response);
  }

  public fiscalCash(operation: FiscalCashOperationEnum, amount: number): Observable<FiscalResponse> {
    return this.api.fiscalCashPost({operation: operation, amount: amount} as FiscalCashRequest).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public scrap(data: Scrap): Observable<FiscalResponse> {
    return this.api.fiscalScrapPost(data).pipe(
      tap({
        next: (fiscalResponse) => this.fiscal = fiscalResponse,
        error: (e) => this.snackService.showBackendError(e)
      })
    );
  }

  public checkout(checkout: CheckoutFiscal): Observable<FiscalResponse> {
    return this.api.fiscalCheckoutPost(checkout).pipe(
      tap({
        next: (fiscalResponse) => {
          this.isOperationPending = true;
          this.token = fiscalResponse.operationToken;
          this.getOperationStatus();
        },
        error: (e) => this.snackService.showBackendError(e)
      })
    );
  }
  public bookingCheckout(baseCheckout: BaseCheckout, bookingId: number): Observable<FiscalResponse> {
    return this.api.fiscalBookingIdCheckoutPost(bookingId, this.languageService.selectedLang, baseCheckout).pipe(
      tap({
        next: (fiscalResponse) => {
          this.isOperationPending = true;
          this.token = fiscalResponse.operationToken;
        },
        error: (e) => this.snackService.showBackendError(e)
      })
    );
  }

  public refund(tickets: RefundTickets): void {
    this.isTransaction = true;
    this.api.fiscalRefundPost(tickets).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    ).subscribe(fiscalResponse => this.fiscal = fiscalResponse);
  }
}
